/**
 * wayai create-credential — create an organization credential (API key, token, or basic auth)
 *
 * Usage:
 *   wayai create-credential --name "openai-key" --type "Bearer Token"
 *   wayai create-credential --name "my-api" --type "Basic Auth" --org <uuid>
 *   echo "$SECRET" | wayai create-credential --name "openai-key" --type "Bearer Token" --stdin
 */

import { requireAuth } from '../lib/auth.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { prompt, promptSecret, readStdin, resolveOrganizationId } from '../lib/utils.js';
import {
  VALID_AUTH_TYPES,
  VALID_ENVIRONMENTS,
  type Environment,
  normalizeCliAuthType,
  normalizeCliEnvironment,
  mapSecretToCredentialFields,
  resolveTagIds,
  splitTagRefs,
} from '../lib/credential-utils.js';

interface ParsedArgs {
  name: string | undefined;
  type: string | undefined;
  orgId: string | undefined;
  description: string | undefined;
  tags: string[];
  environment: string | undefined;
  stdin: boolean;
}

export function parseArgs(args: string[]): ParsedArgs {
  let name: string | undefined;
  let type: string | undefined;
  let orgId: string | undefined;
  let description: string | undefined;
  const tags: string[] = [];
  let environment: string | undefined;
  let stdin = false;

  for (let i = 0; i < args.length; i++) {
    if (args[i] === '--name' && args[i + 1]) {
      name = args[++i];
    } else if (args[i] === '--type' && args[i + 1]) {
      type = args[++i];
    } else if (args[i] === '--org' && args[i + 1]) {
      orgId = args[++i];
    } else if (args[i] === '--description' && args[i + 1]) {
      description = args[++i];
    } else if (args[i] === '--tag' && args[i + 1]) {
      tags.push(args[++i]);
    } else if (args[i] === '--environment' && args[i + 1]) {
      environment = args[++i];
    } else if (args[i] === '--stdin') {
      stdin = true;
    }
  }

  return { name, type, orgId, description, tags: splitTagRefs(tags), environment, stdin };
}

export async function createCredentialCommand(args: string[]): Promise<void> {
  const parsed = parseArgs(args);

  if (!parsed.name) {
    console.error('Missing required flag: --name <credential-name>');
    console.error('Usage: wayai create-credential --name "openai-key" --type "Bearer Token"');
    process.exit(1);
  }

  if (!parsed.type) {
    console.error('Missing required flag: --type <auth-type>');
    console.error(`Supported types: ${VALID_AUTH_TYPES.join(', ')}`);
    process.exit(1);
  }

  // Normalize legacy display names (e.g. "Bearer Token" → "bearer") for backward compat
  const authType = normalizeCliAuthType(parsed.type);
  if (!authType) {
    console.error(`Invalid authentication type: "${parsed.type}"`);
    console.error(`Supported types: ${VALID_AUTH_TYPES.join(', ')}`);
    process.exit(1);
  }

  if (parsed.environment && !normalizeCliEnvironment(parsed.environment)) {
    console.error(`Invalid environment: "${parsed.environment}"`);
    console.error(`Supported environments: ${VALID_ENVIRONMENTS.join(', ')}`);
    process.exit(1);
  }

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  const { organizationId } = await resolveOrganizationId(client, parsed.orgId);

  // Resolve tag names → tag UUIDs (the backend stores credential tags as UUIDs).
  let tagIds: string[] | undefined;
  if (parsed.tags.length > 0) {
    const orgTags = await client.listOrgTags(organizationId);
    tagIds = resolveTagIds(parsed.tags, orgTags.data || []);
  }

  // Collect secret(s) based on auth type
  const secrets: Record<string, string> = {};

  if (parsed.stdin) {
    if (process.stdin.isTTY) {
      console.error('--stdin requires piped input. Example: echo "$SECRET" | wayai create-credential --name "key" --type "Bearer Token" --stdin');
      process.exit(1);
    }
    const input = await readStdin();
    if (!input) {
      console.error('No input received from stdin.');
      process.exit(1);
    }

    try {
      Object.assign(secrets, mapSecretToCredentialFields(authType, input));
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }
  } else {
    // Interactive prompt with masked input
    switch (authType) {
      case 'bearer':
        secrets.access_token = await promptSecret('Enter access token: ');
        break;
      case 'api_key':
        secrets.api_key = await promptSecret('Enter API key: ');
        break;
      case 'basic_auth':
        secrets.username = await prompt('Enter username: ');
        secrets.password = await promptSecret('Enter password: ');
        break;
    }

    // Validate secrets are not empty
    for (const [key, value] of Object.entries(secrets)) {
      if (!value) {
        console.error(`${key} cannot be empty.`);
        process.exit(1);
      }
    }
  }

  console.log(`Creating credential "${parsed.name}" (${authType})...`);

  let result;
  try {
    result = await client.createOrgCredential({
      organization_id: organizationId,
      credential_display_name: parsed.name,
      authentication_type: authType,
      ...(parsed.description && { credential_description: parsed.description }),
      ...(tagIds && tagIds.length > 0 && { tags: tagIds }),
      ...(parsed.environment && { environment: parsed.environment as Environment }),
      ...secrets,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 409) {
      console.error(`\nA credential named "${parsed.name}" already exists in this organization.`);
      console.error(`To rotate its secret or change its tags, use:\n  wayai update-credential --name "${parsed.name}" --stdin`);
      process.exit(1);
    }
    throw err;
  }

  console.log(`\nCredential created: ${result.data.credential_display_name}`);
  console.log(`  ID: ${result.data.organization_credential_id}`);
  console.log(`  Type: ${result.data.authentication_type}`);
}
